"use client";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import CustomTip from "../../common/CustomTip";
import { useEffect, useMemo, useState } from "react";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { TELEGRAM_API_BASE_URL } from "../../utils/constant";
import useApiServices from "../../utils/useApiServices";
import AddMerchantGroup from "./Modals/AddMerchantGroup";
import swal from "sweetalert";
import { Switch, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import styles from "../Tools/Component/MerchantGroupForm.module.css";

const _initialFormData = {
  companyName: "",
  groupChatId: "",
  groupName: "",
  groupType: "",
  mid: "",
  createdBy: "",
  isActive: true,
  isDeleted: false,
};

function Merchantgroup({ toggleRightBar }) {
  const {
    apiCallFnforPost,
    apiCallFnforGet,
    apiCallFnforPut,
    apiCallFnforDelete,
  } = useApiServices();

  const [openMerchant, setOpenMerchant] = useState(false);
  const [formData, setFormData] = useState({ ..._initialFormData });
  const [formErrors, setFormErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [merchantGroups, setMerchantGroups] = useState([]);

  const fetchMerchantGroups = async () => {
    try {
      const response = await apiCallFnforGet(
        `${TELEGRAM_API_BASE_URL}/merchant-group/getAllMerchantGroup`
      );
      if (response?.status === "success") {
        setMerchantGroups(response?.data);
      }
    } catch (err) {
      console.error("Error fetching merchant groups:", err);
    }
  };

  useEffect(() => {
    fetchMerchantGroups();
  }, []);

  const handleChange = (e) => {
    try {
      const { name, value, type, checked } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    } catch (error) {
      console.log(`ERROR WHILE HANDLE CHANGE: ${error}`);
    }
  };

  const openEditModal = (group) => {
    setIsEditing(true);
    setFormData({
      ...group,
      groupChatId: group?.chatId,
      oldMid: group?.mid,
      oldGroupId: group?.chatId,
    });
    setOpenMerchant(true);
  };

  const handleCloseModal = () => {
    setOpenMerchant(false);
    setIsEditing(false);
    setFormErrors({});
    setFormData({ ..._initialFormData });
  };

  const validateMerchantGroup = (data, isEdit = false) => {
    const errors = {};
    if (!data?.companyName?.trim())
      errors.companyName = "Company name is required";
    if (!data?.groupChatId?.trim())
      errors.groupChatId = "Group chat ID is required";
    if (!data?.groupName?.trim()) errors.groupName = "Group name is required";
    if (!data?.groupType?.trim()) errors.groupType = "Group type is required";

    if (!isEdit && !data?.mid?.trim()) errors.mid = "Merchant ID is required";
    if (!isEdit && !data?.createdBy?.trim())
      errors.createdBy = "Created by is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddGroup = async () => {
    setIsSubmitting(true);
    if (!validateMerchantGroup(formData)) {
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await apiCallFnforPost(
        `${TELEGRAM_API_BASE_URL}/merchant-group/addMerchantGroup`,
        formData
      );
      if (response?.status === "success") {
        swal({
          title: "Success!",
          text: response?.message || "Merchant Group added Successfully!",
          icon: "success",
        });
        setOpenMerchant(false);
        setFormData({ ..._initialFormData });
        setFormErrors({});
        fetchMerchantGroups();
      } else {
        swal({
          title: "Failed!",
          text: response?.message || "Failed to add merchant group",
          icon: "error",
        });
      }
    } catch (err) {
      swal({
        title: "Success!",
        text: err?.response?.data?.message || "Failed to add merchant group",
        icon: "error",
      });
      console.error("Error adding merchant group:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditGroup = async () => {
    setIsSubmitting(true);
    if (!validateMerchantGroup(formData, true)) {
      setIsSubmitting(false);
      return;
    }
    try {
      const payload = {
        editCompanyName: formData?.companyName,
        editGroupChatId: formData?.groupChatId,
        editGroupName: formData?.groupName,
        editGroupType: formData?.groupType,
        editMid: formData?.mid,
        isActive: formData?.isActive,
      };
      const response = await apiCallFnforPut(
        `${TELEGRAM_API_BASE_URL}/merchant-group/editMerchantGroupByMid/${formData?.oldGroupId}`,
        payload
      );
      if (response?.status === "success") {
        swal({
          title: "Success!",
          text: response?.message || "Merchant Group Updated Successfully!",
          icon: "success",
        });
        handleCloseModal();
        fetchMerchantGroups();
      } else {
        swal({
          title: "Failed!",
          text: response?.message || "Failed to update merchant group",
          icon: "error",
        });
      }
    } catch (err) {
      console.log("Error updating merchant group:", err);
      swal({
        title: "Failed!",
        text: err?.response?.message || "An error occurred while updating",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    try {
      const response = await apiCallFnforDelete(
        `${TELEGRAM_API_BASE_URL}/merchant-group/delete-merchantGroup?groupChatId=${group?.chatId}`
      );

      if (response?.status === "success") {
        swal({
          title: "Success",
          text: "Merchant Group Deleted Successfully!",
          icon: "success",
        });
        fetchMerchantGroups();
      } else {
        swal({
          title: "Failed",
          text: response?.message || "Failed to delete merchant group",
          icon: "error",
        });
      }
    } catch (err) {
      swal({
        title: "Failed",
        text: err?.response?.message || "Failed To delete Merchant Group.",
        icon: "error",
      });
      console.error("Error deleting merchant group:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleGroupActiveStatus = async (group) => {
    setIsSubmitting(true);
    try {
      const response = await apiCallFnforPut(
        `${TELEGRAM_API_BASE_URL}merchant-group/updateStatus?groupId=${
          group?.chatId
        }&status=${!group?.isActive}`
      );

      if (response?.statusCode === 200) {
        swal("Success", "Group status updated!", "success");
        fetchMerchantGroups();
      } else {
        swal("Failed", response?.message, "error");
      }
    } catch (err) {
      console.error("Error toggling group status:", err);
      swal("Failed", "Something went wrong while toggling status", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      { headerName: "ID", field: "id", flex: 1, minWidth: 80 },
      { headerName: "Group Name", field: "groupName", flex: 1, minWidth: 150 },
      { headerName: "Chat ID", field: "chatId", flex: 1, minWidth: 150 },
      { headerName: "Type", field: "groupType", flex: 1, minWidth: 120 },
      { headerName: "Company", field: "companyName", flex: 1, minWidth: 150 },
      { headerName: "MID", field: "mid", flex: 1, minWidth: 120 },
      {
        headerName: "Status",
        field: "isActive",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <span
            className={`${styles.statusBadge} ${
              params?.value ? styles.active : styles.inactive
            }`}
          >
            {params?.value ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        headerName: "Actions",
        field: "actions",
        align: "center",
        headerAlign: "center",
        minWidth: 150,
        sortable: false,
        renderCell: (params) => (
          <div>
            <Tooltip
              title={
                params?.row?.isActive
                  ? "Click to Deactivate"
                  : "Click to Activate"
              }
            >
              <Switch
                checked={params?.row?.isActive}
                onChange={() => toggleGroupActiveStatus(params?.row)}
                disabled={isSubmitting}
              />
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => openEditModal(params?.row)}
                disabled={isSubmitting}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteGroup(params.row)}
                disabled={isSubmitting}
              >
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [isSubmitting]
  );

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between bg-white">
        <div className="d-flex justify-content-start al ign-items-center">
          <button
            type="button"
            className="btn-close m-3"
            onClick={toggleRightBar}
          ></button>
          <div className="fs-2 text-dark fw-bold">Merchant Group</div>
        </div>

        <div className="d-flex gap-2 justify-content-end me-2 mt-3 mb-2">
          <div className="d-flex gap-2 justify-content-end mt-4 me-4">
            <CustomTip size="18" placement="top" title="Add Merchant Group">
              <div className="">
                <PersonAddAlt1Icon
                  onClick={() => {
                    setOpenMerchant(true);
                  }}
                  style={{ cursor: "pointer" }}
                  fontSize="large"
                />
              </div>
            </CustomTip>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="flex-grow-1 mb-5">
        <DataGrid
          rows={merchantGroups}
          columns={columns}
          getRowId={(row) => row?.id}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          disableRowSelectionOnClick
          loading={isSubmitting}
          sx={{
            height: "100%",
          }}
        />
      </div>

      {/* ADD MERCHANT DIALOG  */}
      <AddMerchantGroup
        formData={formData}
        formErrors={formErrors}
        open={openMerchant}
        isEditing={isEditing}
        onHandleClose={handleCloseModal}
        handleSubmit={() => {
          if (isEditing) {
            handleEditGroup();
          } else {
            handleAddGroup();
          }
        }}
        isSubmitting={isSubmitting}
        handleChange={handleChange}
      />
    </div>
  );
}

export default Merchantgroup;
